import React, { useMemo, useRef, useState } from 'react';
import InsightsFixedTip from './insightsFixedTip';
import { ScoreProgressPoint } from './insightsMatchStats';

interface Props {
    points: ScoreProgressPoint[];
    homeName: string;
    awayName: string;
}

const WIDTH = 720;
const HEIGHT = 220;
const PAD_X = 14;
const PAD_TOP = 20;
const PAD_BOTTOM = 16;

interface PeriodMark {
    /** SVG x position (not goal index). */
    x: number;
    label: string;
}

/**
 * Goal-by-goal lead over the match. Plain SVG on purpose - a chart library would be a new
 * dependency for one step line. ponytail: x axis is goal order, not clock time; upgrade path
 * is scaling x by elapsed seconds once possessions land.
 */
const ScoreEvolutionChart: React.FC<Props> = ({ points, homeName, awayName }) => {
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const chartRef = useRef<SVGSVGElement>(null);

    const layout = useMemo(() => {
        if (points.length < 2) return null;

        const diffs = points.map((p) => p.diff);
        const minDiff = Math.min(...diffs);
        const maxDiff = Math.max(...diffs);
        const range = Math.max(1, maxDiff - minDiff);
        const plotH = HEIGHT - PAD_TOP - PAD_BOTTOM;
        const stepX = (WIDTH - PAD_X * 2) / (points.length - 1);
        const xAt = (index: number): number => PAD_X + index * stepX;
        const yAt = (diff: number): number => PAD_TOP + ((maxDiff - diff) / range) * plotH;

        const periodMarks: PeriodMark[] = [];
        const firstGoal = points.find((p, i) => i > 0 && p.periodId != null);
        if (firstGoal) {
            periodMarks.push({
                x: PAD_X,
                label: firstGoal.periodLabel || `P${firstGoal.periodId}`,
            });
        }
        points.forEach((point, index) => {
            if (index <= 1 || point.periodId == null) return;
            const prev = points[index - 1];
            if (prev.periodId == null || prev.periodId === point.periodId) return;
            periodMarks.push({
                x: (xAt(index - 1) + xAt(index)) / 2,
                label: point.periodLabel || `P${point.periodId}`,
            });
        });

        return {
            minDiff,
            maxDiff,
            xAt,
            yAt,
            yZero: yAt(0),
            periodMarks,
            last: points[points.length - 1],
        };
    }, [points]);

    if (!layout) {
        return <div className="insightsEmpty">No goals logged, so there is nothing to plot.</div>;
    }

    const { minDiff, maxDiff, xAt, yAt, yZero, periodMarks, last } = layout;

    const hover = hoverIndex != null ? points[hoverIndex] : null;
    const tipX = hoverIndex != null && hover ? xAt(hoverIndex) / WIDTH : 0;
    const tipY = hoverIndex != null && hover ? yAt(hover.diff) / HEIGHT : 0;

    return (
        <div className="insightsChart">
            {hover && hoverIndex != null && hoverIndex > 0 ? (
                <InsightsFixedTip
                    x={tipX}
                    y={tipY}
                    container={chartRef.current}
                    className="insightsChartTooltip"
                >
                    <b>
                        {hover.home}:{hover.away}
                    </b>
                    <i>
                        {hover.periodLabel || ''}
                        {hover.clock ? ` · ${hover.clock}` : ''}
                        {hover.isHomeGoal === true
                            ? ` · ${homeName}`
                            : hover.isHomeGoal === false
                            ? ` · ${awayName}`
                            : ''}
                    </i>
                    {hover.scorerName ? <span>Goal: {hover.scorerName}</span> : null}
                    {hover.assistName ? <span>Assist: {hover.assistName}</span> : null}
                    {hover.shotType ? <span>{hover.shotType}</span> : null}
                    {!hover.scorerName && !hover.assistName ? <span>{hover.label}</span> : null}
                </InsightsFixedTip>
            ) : null}
            <svg
                ref={chartRef}
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                preserveAspectRatio="none"
                className="insightsChartSvg"
                role="img"
                aria-label={`Lead over time, final ${last.home} to ${last.away}`}
            >
                {minDiff <= 0 && maxDiff >= 0 ? (
                    <line
                        x1={PAD_X}
                        y1={yZero}
                        x2={WIDTH - PAD_X}
                        y2={yZero}
                        className="insightsChartBaseline"
                    />
                ) : null}
                {periodMarks.map((mark) => (
                    <g key={`period-${mark.x}-${mark.label}`}>
                        <line
                            x1={mark.x}
                            y1={PAD_TOP - 4}
                            x2={mark.x}
                            y2={HEIGHT - 6}
                            className="insightsChartPeriodLine"
                        />
                        <text x={mark.x + 3} y={12} className="insightsChartPeriodLabel">
                            {mark.label}
                        </text>
                    </g>
                ))}
                {points.map((point, index) => {
                    if (index === 0) return null;
                    const prev = points[index - 1];
                    const sideClass =
                        point.isHomeGoal === true
                            ? 'isHome'
                            : point.isHomeGoal === false
                            ? 'isAway'
                            : '';
                    return (
                        <path
                            key={`seg-${index}`}
                            d={`M ${xAt(index - 1)} ${yAt(prev.diff)} L ${xAt(index)} ${yAt(
                                prev.diff
                            )} L ${xAt(index)} ${yAt(point.diff)}`}
                            className={`insightsChartLine insightsChartSeg ${sideClass}`}
                        />
                    );
                })}
                {points.map((point, index) => {
                    if (index === 0) return null;
                    const sideClass =
                        point.isHomeGoal === true
                            ? 'isHome'
                            : point.isHomeGoal === false
                            ? 'isAway'
                            : '';
                    return (
                        <g key={`${index}-${point.label}`}>
                            <circle
                                cx={xAt(index)}
                                cy={yAt(point.diff)}
                                r={hoverIndex === index ? 5 : 3.5}
                                className={`insightsChartDot ${sideClass}${
                                    hoverIndex === index ? ' isActive' : ''
                                }`}
                            />
                            <circle
                                cx={xAt(index)}
                                cy={yAt(point.diff)}
                                r={10}
                                className="insightsTrendHit"
                                onMouseEnter={() => setHoverIndex(index)}
                                onMouseLeave={() => setHoverIndex(null)}
                            />
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

export default ScoreEvolutionChart;
